import {
  ColumnDef,
  functionalUpdate,
  InitialTableState,
  PaginationState,
  Row,
  RowSelectionState,
  TableOptions,
  Updater,
} from '@tanstack/react-table';
import {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useActiveSortTableColumnContext } from 'src/contexts/ActiveSortTableColumnContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useCatalogMetricsContext } from 'src/contexts/CatalogMetricsContext';
import { createAggregatedListingDetailedMetrics } from 'src/contexts/CatalogMetricsContext/CatalogMetrics.utils';
import {
  Content,
  FormatContent,
  useContent,
} from 'src/contexts/ContentContext';
import { useEventHubContext } from 'src/contexts/EventHubContext';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import {
  MultiSelectScope,
  useMultiSelectionContext,
} from 'src/contexts/MultiSelectionContext';
import { useClearShiftSelectionOnSortingChange } from 'src/contexts/MultiSelectionContext/useClearShiftSelectionOnSortingChange';
import { useTagsForEntityType } from 'src/hooks/useTagsForEntityType';
import {
  useUserHasFeature,
  useUserHasTableTotalsFeature,
} from 'src/hooks/useUserHasFeature';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { NoData, Table } from 'src/tables/Table';
import {
  defaultEventsColumnsConfig,
  filterColumnsByFeatures,
  getEventsColumnConfigById,
} from 'src/utils/columns/columnUtils';
import { EventsTableColumnId } from 'src/utils/columns/events/eventsColumnUtils.types';
import { CustomEventsColumn } from 'src/utils/columns/events/eventsCustomColumnUtils.types';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { QueryWithViewMode } from 'src/utils/eventQueryUtils';
import { getPerformerAndVenueForEvent } from 'src/utils/eventWithDataUtils';
import { SectionType } from 'src/utils/types/sectionType';
import { hasFeatures } from 'src/utils/userUtils';
import { SomethingWentWrong } from 'src/views';
import {
  ActionOutboxEntityType,
  EntityWithTicketsQuery,
  EventWithData,
  Feature,
  ListingDetailedMetrics,
  UserSetting,
} from 'src/WebApiController';

import { isCustomEventsColumn } from '../../utils/columns/events/eventsCustomColumnUtils';
import {
  customColumnDef,
  EventWithDataAndMetrics,
  getEventsTableColumnsConfig,
  tagColumnDef,
} from './configs/EventsTableColumnsConfig';

export type EventsTableProps = {
  events: EventWithData[];
  highlightedId?: string;
  failedToRetrieveData: boolean;
  onMount?: () => void;
  onUnmount?: (state: object) => void;
  initState?: InitialTableState;
  useVirtuoso?: boolean;
  usePagination?: boolean;
  useDataGrouping?: boolean;
  withOuterPadding?: boolean;
  tableHeadStyle?: CSSProperties;
  scrollToEventVirtId?: string;
};

const PAGE_SIZE = 100;

const getRowId = (originalRow: EventWithDataAndMetrics | null): string => {
  return (originalRow as EventWithDataAndMetrics)?.event.virtId ?? '';
};

export const EventsTable = ({
  events,
  highlightedId,
  failedToRetrieveData,
  onMount,
  onUnmount,
  initState,
  useVirtuoso,
  usePagination = true,
  useDataGrouping,
  withOuterPadding,
  tableHeadStyle,
  scrollToEventVirtId,
}: EventsTableProps) => {
  const hasTableTotalsFeature = useUserHasTableTotalsFeature(
    ActionOutboxEntityType.SellerEvent
  );
  const hasCompanionModeInInsightViewFeature = useUserHasFeature(
    Feature.CompanionModeInInsightView
  );
  const hasInsightsViewTableFiltersFeature = useUserHasFeature(
    Feature.InsightsViewTableFilters
  );

  const { onSelectEvent } = useEventHubContext();

  const eventsText = useContent(ContentId.Events);
  const { appContext, loginContext } = useAppContext();
  const { getSelection, selectionMode, setGroupStates } =
    useMultiSelectionContext();
  const listingSelection = getSelection();
  const { filterQuery } = useFilterQueryContext<
    EntityWithTicketsQuery & QueryWithViewMode
  >();
  const { eventDetailedMetrics: detailedMetrics } =
    useCatalogMetricsContext<ListingDetailedMetrics>();

  const { data: catalogData } = useCatalogDataContext();

  const singlePerformer = filterQuery.performerIds?.length === 1;

  // Enable passing in table state as parameters -- we can remount with the last state the user was on
  const { sorting, setSorting } = useActiveSortTableColumnContext();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: initState?.pagination?.pageIndex || 0,
    pageSize: PAGE_SIZE,
  });
  const grouping = useMemo(() => {
    return sorting.map((sort) => sort.id);
  }, [sorting]);

  useClearShiftSelectionOnSortingChange({ sortingState: sorting });

  useEffect(() => {
    onMount?.();
  });

  useEffect(() => {
    /**
     * Intent for `onMount` is use with 'windowing' in order to maintain user state
     * when the component is scrolled back into view.
     * Anything that needs to be persisted in `react-table` state should be added here.
     * Only update on unmount to ensure we aren't doing too many re-renders.
     */
    return () => onUnmount?.({ pagination, sorting });
  }, [pagination, sorting, onUnmount]);

  const data = useMemo(
    () =>
      events.map((event) => {
        const metrics = detailedMetrics?.[
          event.event.virtId
        ] as ListingDetailedMetrics;
        const { performer, venue } = getPerformerAndVenueForEvent(
          event.event,
          catalogData
        );
        return {
          ...event,
          performer,
          venue,
          isSinglePerformerView: singlePerformer,
          metrics,
        };
      }),
    [catalogData, detailedMetrics, events, singlePerformer]
  );

  // TODO: handle main columns filter
  const { value: storedEventsColumnOrderSetting = defaultEventsColumnsConfig } =
    useServerUserSetting<EventsTableColumnId[]>({
      id: UserSetting.EventsColumnOrder,
    });
  const { value: storedEventsColumnsEnabledSetting } = useServerUserSetting<
    string[]
  >({
    id: UserSetting.EventsColumnsEnabled,
  });
  const { value: customEventsColumns = [] } = useServerUserSetting<
    CustomEventsColumn[]
  >({
    id: UserSetting.EventsCustomColumns,
  });

  const { tagsMetadata } = useTagsForEntityType(
    ActionOutboxEntityType.SellerEvent,
    true
  );

  const eventsTableColumnsConfig = useMemo(
    () => getEventsTableColumnsConfig(hasInsightsViewTableFiltersFeature),
    [hasInsightsViewTableFiltersFeature]
  );

  const displayedColumnsConfig = useMemo(() => {
    const eventsColumnOrder = storedEventsColumnOrderSetting.filter(
      // If storedEventsColumnsEnabledSetting has never been set, default to true
      (c) => storedEventsColumnsEnabledSetting?.includes(c) ?? true
    );

    const columns = new Set([
      // Add unconfigurable columns first
      ...Object.values(eventsTableColumnsConfig)
        .filter((column) => {
          const columnDef = getEventsColumnConfigById(
            column.id as EventsTableColumnId
          ).personalization;
          const hasFeatureForColumn =
            columnDef.requiredFeatures.length === 0 ||
            hasFeatures(
              loginContext?.user,
              appContext?.features,
              columnDef.requiredFeatures
            );

          const isConfigurable = columnDef.isConfigurable;

          return !isConfigurable && hasFeatureForColumn;
        })
        .map((item) => item.id as EventsTableColumnId),
      // Add user defined columns next
      ...filterColumnsByFeatures(
        eventsColumnOrder,
        SectionType.Events,
        customEventsColumns,
        loginContext?.user,
        appContext?.features
      ),
    ]);

    return Array.from(columns).reduce<
      ColumnDef<EventWithDataAndMetrics | null>[]
    >((acc, columnId) => {
      if (tagsMetadata?.find((t) => t.key === columnId)) {
        acc.push(tagColumnDef(columnId, hasInsightsViewTableFiltersFeature));
      } else if (isCustomEventsColumn(columnId)) {
        acc.push(
          customColumnDef(
            columnId,
            customEventsColumns.find((c) => c.id === columnId)?.formula,
            hasInsightsViewTableFiltersFeature
          )
        );
      } else {
        const columnConfig = Object.values(eventsTableColumnsConfig).find(
          (column) => column.id === columnId
        );
        if (columnConfig) acc.push(columnConfig);
      }

      return acc;
    }, []);
  }, [
    appContext?.features,
    customEventsColumns,
    loginContext?.user,
    storedEventsColumnOrderSetting,
    storedEventsColumnsEnabledSetting,
    tagsMetadata,
    eventsTableColumnsConfig,
    hasInsightsViewTableFiltersFeature,
  ]);

  const rowSelection = useMemo(() => {
    if (!selectionMode && highlightedId) {
      return {
        [highlightedId]: true,
      };
    }
    return listingSelection.groupIds.reduce<Record<string, boolean>>(
      (acc, eventId) => {
        acc[eventId] = true;
        return acc;
      },
      {}
    );
  }, [highlightedId, listingSelection.groupIds, selectionMode]);

  const onRowSelectionChanged = useCallback(
    (updater: Updater<RowSelectionState>) => {
      const newSelections =
        typeof updater === 'function' ? updater(rowSelection) : updater;
      const newSelectedIds = Object.keys(newSelections).filter(
        (r) => newSelections[r] === true
      );
      if (!selectionMode) {
        newSelectedIds.forEach((id) => {
          if (rowSelection[id] && newSelections[id]) {
            // if both of these are the same value and is true, we need to set it to false
            newSelections[id] = false;
          }
        });
      }
      setGroupStates(newSelections);
    },
    [rowSelection, selectionMode, setGroupStates]
  );

  const showCheckbox = selectionMode?.mode === MultiSelectScope.AllGroups;

  const summaryData = useMemo(() => {
    if (!hasTableTotalsFeature || !data) {
      return null;
    }
    return {
      metrics: createAggregatedListingDetailedMetrics(
        data.map((d) => d.metrics).filter((m) => m != null)
      ),
    } as EventWithDataAndMetrics;
  }, [data, hasTableTotalsFeature]);

  const [filteredSummaryData, setFilteredSummaryData] =
    useState<EventWithDataAndMetrics | null>(null);

  const onFilteredRowsChange = useCallback(
    (filteredRows: Row<EventWithDataAndMetrics | null>[] | undefined) => {
      if (
        !hasTableTotalsFeature ||
        !hasInsightsViewTableFiltersFeature ||
        !filteredRows
      ) {
        setFilteredSummaryData(null);
        return;
      }

      const filteredMetrics = filteredRows
        .map((row) => row.original?.metrics)
        .filter((m) => m != null);

      setFilteredSummaryData({
        metrics: createAggregatedListingDetailedMetrics(filteredMetrics),
      } as EventWithDataAndMetrics);
    },
    [hasTableTotalsFeature, hasInsightsViewTableFiltersFeature]
  );

  const options: Partial<TableOptions<EventWithDataAndMetrics | null>> =
    useMemo(
      () => ({
        data,
        columns: displayedColumnsConfig,
        state: {
          expanded: useDataGrouping ? true : undefined,
          grouping,
          pagination: usePagination ? pagination : undefined,
          sorting,
          rowSelection,
          columnVisibility: {
            checkbox: showCheckbox,
          },
          columnPinning: {
            right: [EventsTableColumnId.Action],
          },
        },
        meta: {
          sectionType: SectionType.Events,
          summaryData: hasInsightsViewTableFiltersFeature
            ? (filteredSummaryData ?? summaryData)
            : summaryData,
        },
        getRowId: getRowId,
        onRowSelectionChange: onRowSelectionChanged,
        onPaginationChange: usePagination ? setPagination : undefined,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onSortingChange: (sortingUpdaterFn: any) => {
          const newSortVal = functionalUpdate(sortingUpdaterFn, sorting);
          setSorting(newSortVal);
        },
        enableRowSelection: showCheckbox,
        enableMultiRowSelection: showCheckbox,
        enableGrouping: !!useDataGrouping,
      }),
      [
        data,
        displayedColumnsConfig,
        useDataGrouping,
        grouping,
        usePagination,
        pagination,
        sorting,
        rowSelection,
        showCheckbox,
        hasInsightsViewTableFiltersFeature,
        filteredSummaryData,
        summaryData,
        onRowSelectionChanged,
        setSorting,
      ]
    );

  const onRowSelect = useCallback(
    (tableRows: Row<any>[] | undefined, row: Row<any>, index: number) => {
      if (hasCompanionModeInInsightViewFeature) {
        row.toggleExpanded();
        onSelectEvent(row.id);
      }
    },
    [hasCompanionModeInInsightViewFeature, onSelectEvent]
  );
  const onRowShiftSelect = useCallback(
    (tableRows: Row<any>[] | undefined, row: Row<any>, index: number) => {
      if (hasCompanionModeInInsightViewFeature) {
        onSelectEvent(row.id);
      }
    },
    [hasCompanionModeInInsightViewFeature, onSelectEvent]
  );

  return failedToRetrieveData ? (
    <SomethingWentWrong
      message={<Content id={ContentId.FailToLoadListContent} />}
    />
  ) : data?.length > 0 ? (
    <Table
      options={options}
      tableLayout="fixed"
      tableHeadStyle={{ top: '56px', ...tableHeadStyle }}
      useVirtuoso={useVirtuoso}
      usePaginationFooter={usePagination}
      useDataGrouping={useDataGrouping}
      withOuterPadding={withOuterPadding}
      disableFooter={!summaryData}
      scrollToItemRowId={scrollToEventVirtId}
      onRowSelect={onRowSelect}
      onRowShiftSelect={onRowShiftSelect}
      enableColumnFilters={hasInsightsViewTableFiltersFeature}
      onFilteredRowsChange={
        hasInsightsViewTableFiltersFeature ? onFilteredRowsChange : undefined
      }
    />
  ) : (
    // We should never see this - because the events are filtered to only those that has sales
    // So this is to prevent a crash - but this should be looked at
    <NoData>
      <FormatContent id={FormatContentId.NoDataAvailable} params={eventsText} />
    </NoData>
  );
};
